import { createSelector } from 'reselect'
import * as Process from './process'
import * as Schema from './schema'
import * as Dimensions from './dimensions'
import * as File from './file'
import * as Stages from './stages'
import { getValidator } from './validate'
import * as selfExports from './status'
import { getArgN, makeAPI } from './utils'

function hasOwnError (status) {
  if (status.ignored) return false
  if (status.parentStatus && status.parentStatus.hasOwnError) {
    return false
  }
  return !!(
    !status.isInputValid ||
    !status.isValueValid ||
    (status.analyses && !status.isRunIDValid) ||
    !status.areDimensionsValid ||
    status.needsMissingFile ||
    status.hasInvalidFileType
  )
}

function isAutoDisabled (status) {
  const { parentStatus } = status
  return !!(
    !status.ignored && (parentStatus && (
      !status.parentStatus.isValid
    ))
  )
}

function isValid (status) {
  return (
    !status.isAutoDisabled &&
    !status.hasOwnError
  )
}

export function setStatusFields (status) {
  status = Object.assign({}, status)
  status.hasOwnError = hasOwnError(status)
  status.isAutoDisabled = isAutoDisabled(status)
  status.isValid = isValid(status)
  return status
}

export const getAPI = makeAPI(selfExports, [
  Schema.getAPI,
  File.getAPI,
  Process.getAll
])

export const getLastNode = createSelector([ Process.get, Stages.getAll ], (process, stages) => {
  const lastStage = stages[stages.length - 1]
  return lastStage.node
})

export const getStages = createSelector([ Stages.getAll, Dimensions.getAll ], (_stages, dimensions) => {
  return _stages.map(stage => {
    const d = dimensions[stage.node.id]
    return Object.assign({}, stage, {
      dimensions: d,
      totalVoxels: d ? d.size[0] * d.size[1] * d.size[2] : 0
    })
  })
})

export const getDatatypes = createSelector([ Schema.getAPI, File.getAPI, Stages.getAll ], (SchemaAPI, FileAPI, stages) => {
  let input = ''
  return stages.reduce((types, { node }, index) => {
    if (index === 0) {
      const filename = node.value['inputdata/filename']
      const output = FileAPI.getDatatype(filename)
      input = output
      return Object.assign(types, {
        [node.id]: { input, output }
      })
    } else {
      if (!input) {
        return Object.assign(types, {
          [node.id]: { input: '', output: '' }
        })
      }

      const output = SchemaAPI.getOutputType(node.schemaID, input)

      const result = Object.assign(types, {
        [node.id]: { input, output }
      })
      if (!node.ignored) input = output
      return result
    }
  }, {})
})

const DEFAULT_MILLISECONDS_PER_VOXEL = [0.002, 0.003, 0.004]

export const getAll = createSelector([
  Schema.getAPI,
  getStages,
  getDatatypes,
  getValidator
], (SchemaAPI, stages, stageTypes, validate) => {
  let nodeIndex = 0
  let previousStage = null
  return stages.reduce((statuses, stage, index) => {
    const { node, schema, analyses } = stage
    const inputNodeID = previousStage && previousStage.node.id
    if (!node.ignored) previousStage = stage

    const previousStatus = statuses[inputNodeID]

    const types = stageTypes[node.id]
    const isInputValid = !!types.output
    const enabled = stage.enabled
    const ignored = !!node.ignored
    const errors = validate(node.value, schema)
    const isValueValid = !Object.keys(errors).length
    const nodeStatus = Object.assign({}, setStatusFields({
      id: node.id,
      schemaID: node.schemaID,
      schemaType: schema.type,
      schemaName: schema.name,
      inputNodeID,
      nodeIndex: nodeIndex++,
      canBeRunStart: stage.canBeRunStart,
      canBeRunEnd: stage.canBeRunEnd,
      isRunStart: stage.isRunStart,
      isRunEnd: stage.isRunEnd,
      runID: stage.runID,
      dimensions: stage.dimensions,
      areDimensionsValid: areDimensionsValid(stage.dimensions),
      enabled,
      totalVoxels: stage.totalVoxels,
      estimate: getEstimate(schema, { totalVoxels: stage.totalVoxels }),
      isFirstStage: index === 1,
      isLastStage: index === stages.length - 1,
      allInRun: stage.allInRun,
      isInRun: stage.isInRun,
      inputType: types.input,
      outputType: types.output,
      ignored,
      parentStatus: previousStatus,
      canToggleDisabled: stage.isInRun,
      errors,
      isRunIDValid: stage.isRunIDValid,
      isValueValid,
      isInputValid,
      analyses: analyses.map(data => data.node.id)
    }))

    statuses[node.id] = nodeStatus

    const parentNode = node
    const parentStatus = nodeStatus
    analyses.forEach(({ node: analysisNode, schema }, index) => {
      const errors = validate(analysisNode.value, schema)
      const isValueValid = !Object.keys(errors).length
      const isInputValid = !!SchemaAPI.getOutputType(analysisNode.schemaID, parentStatus.outputType)
      const ignored = !!analysisNode.ignored
      statuses[analysisNode.id] = Object.assign({}, setStatusFields({
        id: analysisNode.id,
        schemaID: analysisNode.schemaID,
        schemaType: schema.type,
        schemaName: schema.name,
        inputNodeID: parentNode.id,
        nodeIndex: nodeIndex++,
        isFirstAnalysis: index === 0,
        isLastAnalysis: index === analyses.length - 1,
        inputType: parentStatus.outputType,
        outputType: parentStatus.outputType,
        dimensions: parentStatus.dimensions,
        totalVoxels: parentStatus.totalVoxels,
        estimate: getEstimate(schema, { totalVoxels: stage.totalVoxels }),
        canToggleDisabled: parentStatus.enabled,
        ignored,
        errors,
        parentStatus,
        isValueValid,
        isInputValid,
        areDimensionsValid: parentStatus.areDimensionsValid,
        get enabled () {
          return (
            parentStatus.isInRun &&
            !analysisNode.disabled
          )
        }
      }))
    })

    return statuses
  }, {})
})

function areDimensionsValid (dimensions) {
  return (
    dimensions.size.every(size => size !== -1) &&
    dimensions.origin.every(size => size !== -1) &&
    dimensions.stride.every(size => size !== -1)
  )
}

function getEstimate (schema, { totalVoxels }) {
  const { bytesPerVoxel, millisecondsPerVoxel = DEFAULT_MILLISECONDS_PER_VOXEL } = schema
  return {
    time: millisecondsPerVoxel ? millisecondsPerVoxel.map(v => v * totalVoxels) : [0, 0, 0],
    memory: bytesPerVoxel ? bytesPerVoxel.map(v => v * totalVoxels) : [0, 0, 0]
  }
}

export const get = createSelector([ getAll, getArgN(2) ], (statuses, nodeID) => {
  return statuses[nodeID]
})



// WEBPACK FOOTER //
// ../webmango-core/selectors/status.js